import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  Typography,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormControlLabel,
  Checkbox,
  IconButton,
  CircularProgress,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableRow,
  TableHead,
  TableContainer,
  Paper,
  InputAdornment,
  Tooltip,
  Alert,
  Collapse,
  alpha
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import AddIcon from '@mui/icons-material/Add';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import QuizIcon from '@mui/icons-material/Quiz';
import SearchIcon from '@mui/icons-material/Search';
import GroupIcon from '@mui/icons-material/Group';
import SchoolIcon from '@mui/icons-material/School';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';

const API_URL = process.env.REACT_APP_API_URL || '';

function getAdminToken() {
  return localStorage.getItem('gogig_admin_access') || localStorage.getItem('gogig_admin_token');
}

function authHeaders() {
  const token = getAdminToken();
  return { headers: { Authorization: `Bearer ${token}` } };
}

function parseYouTubeId(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  const m = u.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]{11})/);
  return m ? m[1] : null;
}

function parseBunnyUrl(url) {
  if (!url || typeof url !== 'string') return null;
  const u = url.trim();
  if (/mediadelivery\.net\/embed\//i.test(u)) return u;
  if (/mediadelivery\.net|bunny\.net|b-cdn\.net/i.test(u)) return u;
  if (/\.m3u8|playlist/i.test(u)) return u; // HLS
  return null;
}

function AdminCourseManagement() {
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState('');
  const [search, setSearch] = useState('');
  const [createOpen, setCreateOpen] = useState(false);
  const [editCourse, setEditCourse] = useState(null);
  const [form, setForm] = useState({
    title: '',
    description: '',
    content_type: 'text',
    video_url: '',
    content_duration: '',
    duration_hours: '',
    category: '',
    level: 'beginner',
    status: 'active',
    passing_score: 70,
    has_assessment: false
  });
  const [contentModalOpen, setContentModalOpen] = useState(false);
  const [contentCourseId, setContentCourseId] = useState(null);
  const [chapters, setChapters] = useState([]);
  const [chaptersLoading, setChaptersLoading] = useState(false);
  const [questionsModalOpen, setQuestionsModalOpen] = useState(false);
  const [questionsCourseId, setQuestionsCourseId] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [questionsLoading, setQuestionsLoading] = useState(false);
  const [newQuestionText, setNewQuestionText] = useState('');
  const [newQuestionOptions, setNewQuestionOptions] = useState([{ option_text: '', is_correct: false }, { option_text: '', is_correct: false }]);
  const [submitting, setSubmitting] = useState(false);
  const [newChapterTitle, setNewChapterTitle] = useState('');
  const [newVideoUrl, setNewVideoUrl] = useState({});
  const [newVideoTitle, setNewVideoTitle] = useState({});
  const [savingContent, setSavingContent] = useState(false);
  // Enrollments
  const [enrollmentsModalOpen, setEnrollmentsModalOpen] = useState(false);
  const [enrollmentsCourse, setEnrollmentsCourse] = useState(null);
  const [enrollments, setEnrollments] = useState([]);
  const [enrollmentsLoading, setEnrollmentsLoading] = useState(false);
  // Per-video quiz questions
  const [videoQuizModalOpen, setVideoQuizModalOpen] = useState(false);
  const [videoQuizVideo, setVideoQuizVideo] = useState(null);
  const [videoQuizQuestions, setVideoQuizQuestions] = useState([]);
  const [videoQuizLoading, setVideoQuizLoading] = useState(false);
  const [newVideoQuestionText, setNewVideoQuestionText] = useState('');
  const [newVideoQuestionTime, setNewVideoQuestionTime] = useState('');
  const [newVideoQuestionOptions, setNewVideoQuestionOptions] = useState([
    { text: '', is_correct: false },
    { text: '', is_correct: false }
  ]);

  const fetchCourses = async () => {
    setLoading(true);
    try {
      const q = new URLSearchParams();
      if (statusFilter) q.set('status', statusFilter);
      if (search) q.set('search', search);
      const res = await fetch(`${API_URL}/api/courses/admin/courses?${q}`, authHeaders());
      const data = await res.json();
      if (data.success && data.courses) setCourses(data.courses);
      else setCourses([]);
    } catch (e) {
      setCourses([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCourses();
  }, [statusFilter, search]);

  const openCreate = () => {
    setForm({
      title: '',
      description: '',
      content_type: 'text',
      video_url: '',
      content_duration: '',
      duration_hours: '',
      category: '',
      level: 'beginner',
      status: 'active',
      passing_score: 70,
      has_assessment: false
    });
    setEditCourse(null);
    setCreateOpen(true);
  };

  const openEdit = (c) => {
    setEditCourse(c);
    setForm({
      title: c.title || '',
      description: c.description || '',
      content_type: c.content_type || 'text',
      video_url: c.video_url || '',
      content_duration: c.content_duration ?? '',
      duration_hours: c.duration_hours ?? '',
      category: c.category || '',
      level: c.level || 'beginner',
      status: c.status || 'active',
      passing_score: c.passing_score ?? 70,
      has_assessment: !!c.has_assessment
    });
    setCreateOpen(true);
  };

  const handleSaveCourse = async () => {
    setSubmitting(true);
    try {
      const payload = {
        title: form.title || 'Untitled',
        description: form.description || null,
        content_type: form.content_type || 'text',
        video_url: form.video_url || null,
        content_duration: form.content_duration ? Number(form.content_duration) : null,
        duration_hours: form.duration_hours ? Number(form.duration_hours) : null,
        category: form.category || null,
        level: form.level || 'beginner',
        status: form.status || 'active',
        passing_score: form.passing_score != null ? Number(form.passing_score) : 70,
        has_assessment: form.has_assessment
      };
      if (editCourse) {
        const res = await fetch(`${API_URL}/api/courses/admin/courses/${editCourse.id}`, {
          method: 'PUT',
          ...authHeaders(),
          headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setCreateOpen(false);
          fetchCourses();
        }
      } else {
        const res = await fetch(`${API_URL}/api/courses/admin/courses`, {
          method: 'POST',
          ...authHeaders(),
          headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await res.json();
        if (res.ok && data.success) {
          setCreateOpen(false);
          fetchCourses();
          if (form.has_assessment && data.course?.id) {
            openQuestionsModal(data.course.id);
          }
        }
      }
    } catch (e) {}
    setSubmitting(false);
  };

  const handleDeleteCourse = async (id) => {
    if (!window.confirm('Delete this course?')) return;
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/courses/${id}`, { method: 'DELETE', ...authHeaders() });
      if (res.ok) fetchCourses();
    } catch (e) {}
  };

  const openEnrollmentsModal = async (course) => {
    setEnrollmentsCourse(course);
    setEnrollmentsModalOpen(true);
    setEnrollments([]);
    setEnrollmentsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/courses/${course.id}/enrollments`, authHeaders());
      const data = await res.json();
      if (data.success && data.enrollments) setEnrollments(data.enrollments);
      else setEnrollments([]);
    } catch (e) {
      setEnrollments([]);
    } finally {
      setEnrollmentsLoading(false);
    }
  };

  const openContentModal = async (courseId) => {
    setContentCourseId(courseId);
    setContentModalOpen(true);
    setChapters([]);
    setNewVideoUrl({});
    setChaptersLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/courses/${courseId}/chapters`, authHeaders());
      const data = await res.json();
      if (data.success && data.chapters) setChapters(data.chapters);
      else setChapters([]);
    } catch (e) {
      setChapters([]);
    } finally {
      setChaptersLoading(false);
    }
  };

  const addChapter = async () => {
    if (!contentCourseId || !newChapterTitle.trim()) return;
    setSavingContent(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/courses/${contentCourseId}/chapters`, {
        method: 'POST',
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ title: newChapterTitle.trim(), order_index: chapters.length })
      });
      const data = await res.json();
      if (data.success && data.chapter) {
        setChapters((prev) => [...prev, { ...data.chapter, videos: [] }]);
        setNewChapterTitle('');
      }
    } catch (e) {}
    setSavingContent(false);
  };

  const addVideoToChapter = async (chapterId) => {
    const title = (newVideoTitle[chapterId] || '').trim() || 'Untitled Video';
    const url = (newVideoUrl[chapterId] || '').trim();
    const videoId = parseYouTubeId(url);
    const bunnyUrl = parseBunnyUrl(url);
    if (!contentCourseId || !chapterId || (!videoId && !bunnyUrl && !url)) return;
    setSavingContent(true);
    const isYouTube = !!videoId;
    const isBunny = !isYouTube && (!!bunnyUrl || !!url);
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/chapters/${chapterId}/videos`, {
        method: 'POST',
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title,
          video_provider: isYouTube ? 'youtube' : isBunny ? 'bunny' : 'other',
          video_id: videoId || null,
          video_url: isYouTube ? null : url,
          order_index: 0
        })
      });
      const data = await res.json();
      if (data.success && data.video) {
        setChapters((prev) => prev.map((ch) => (ch.id === chapterId ? { ...ch, videos: [...(ch.videos || []), data.video] } : ch)));
        setNewVideoUrl((prev) => ({ ...prev, [chapterId]: '' }));
        setNewVideoTitle((prev) => ({ ...prev, [chapterId]: '' }));
      }
    } catch (e) {}
    setSavingContent(false);
  };

  const openQuestionsModal = async (courseId) => {
    setQuestionsCourseId(courseId);
    setQuestionsModalOpen(true);
    setQuestions([]);
    setNewQuestionText('');
    setNewQuestionOptions([{ option_text: '', is_correct: false }, { option_text: '', is_correct: false }]);
    setQuestionsLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/courses/${courseId}/questions`, authHeaders());
      const data = await res.json();
      if (data.success && data.questions) setQuestions(data.questions);
      else setQuestions([]);
    } catch (e) {
      setQuestions([]);
    } finally {
      setQuestionsLoading(false);
    }
  };

  const addQuestion = async () => {
    if (!questionsCourseId || !newQuestionText.trim()) return;
    const opts = newQuestionOptions.filter((o) => (o.option_text || '').trim());
    if (opts.length < 2) return;
    setSubmitting(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/courses/${questionsCourseId}/questions`, {
        method: 'POST',
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_text: newQuestionText.trim(),
          order_index: questions.length,
          options: opts.map((o, i) => ({ option_text: o.option_text.trim(), is_correct: !!o.is_correct, order_index: i }))
        })
      });
      const data = await res.json();
      if (data.success && data.question) {
        setQuestions((prev) => [...prev, data.question]);
        setNewQuestionText('');
        setNewQuestionOptions([{ option_text: '', is_correct: false }, { option_text: '', is_correct: false }]);
      }
    } catch (e) {}
    setSubmitting(false);
  };

  const deleteQuestion = async (qid) => {
    if (!questionsCourseId || !window.confirm('Delete this question?')) return;
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/courses/${questionsCourseId}/questions/${qid}`, { method: 'DELETE', ...authHeaders() });
      if (res.ok) setQuestions((prev) => prev.filter((q) => q.id !== qid));
    } catch (e) {}
  };

  const openVideoQuizModal = async (video) => {
    setVideoQuizVideo(video);
    setVideoQuizModalOpen(true);
    setVideoQuizQuestions([]);
    setNewVideoQuestionText('');
    setNewVideoQuestionTime('');
    setNewVideoQuestionOptions([{ text: '', is_correct: false }, { text: '', is_correct: false }]);
    setVideoQuizLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/courses/admin/videos/${video.id}/questions`, authHeaders());
      const data = await res.json();
      if (data.success && data.questions) setVideoQuizQuestions(data.questions);
      else setVideoQuizQuestions([]);
    } catch (e) {
      setVideoQuizQuestions([]);
    } finally {
      setVideoQuizLoading(false);
    }
  };

  const addVideoQuestion = async () => {
    if (!videoQuizVideo || !newVideoQuestionText.trim()) return;
    const opts = newVideoQuestionOptions.filter((o) => (o.text || '').trim());
    if (opts.length < 2) return;
    const correctCount = opts.filter((o) => o.is_correct).length;
    if (correctCount !== 1) {
      window.alert('Mark exactly one option as correct.');
      return;
    }
    setSubmitting(true);
    try {
      const timeSec = newVideoQuestionTime.trim() ? parseInt(newVideoQuestionTime, 10) : null;
      const res = await fetch(`${API_URL}/api/courses/admin/videos/${videoQuizVideo.id}/questions`, {
        method: 'POST',
        ...authHeaders(),
        headers: { ...authHeaders().headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          question_text: newVideoQuestionText.trim(),
          order_index: videoQuizQuestions.length,
          time_in_video_seconds: Number.isFinite(timeSec) && timeSec >= 0 ? timeSec : null,
          options: opts.map((o) => ({ text: o.text.trim(), is_correct: !!o.is_correct }))
        })
      });
      const data = await res.json();
      if (data.success && data.question) {
        setVideoQuizQuestions((prev) => [...prev, data.question]);
        setNewVideoQuestionText('');
        setNewVideoQuestionTime('');
        setNewVideoQuestionOptions([{ text: '', is_correct: false }, { text: '', is_correct: false }]);
      }
    } catch (e) {}
    setSubmitting(false);
  };

  const deleteVideoQuestion = async (questionId) => {
    if (!videoQuizVideo || !window.confirm('Delete this video question?')) return;
    try {
      const res = await fetch(
        `${API_URL}/api/courses/admin/videos/${videoQuizVideo.id}/questions/${questionId}`,
        { method: 'DELETE', ...authHeaders() }
      );
      if (res.ok) setVideoQuizQuestions((prev) => prev.filter((q) => q.id !== questionId));
    } catch (e) {}
  };

  const statusColor = (status) => {
    if (status === 'active') return 'success';
    if (status === 'inactive') return 'default';
    return 'warning';
  };

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <SchoolIcon sx={{ fontSize: 36, color: '#0f766e' }} />
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, color: '#0f766e' }}>Course Management</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Create courses, add chapters &amp; videos, manage assessments, and view enrollments.</Typography>
          </Box>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' }, fontWeight: 600 }}>Create Course</Button>
      </Box>

      <Card sx={{ p: 2, mb: 3, borderRadius: 2, boxShadow: '0 1px 3px rgba(0,0,0,0.08)' }}>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search by title or description..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            sx={{ minWidth: 260 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start"><SearchIcon fontSize="small" color="action" /></InputAdornment>
              )
            }}
          />
          <FormControl size="small" sx={{ minWidth: 140 }}>
            <InputLabel>Status</InputLabel>
            <Select value={statusFilter} label="Status" onChange={(e) => setStatusFilter(e.target.value)}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>
        </Box>
      </Card>

      {loading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
      )}
      {!loading && courses.length === 0 && (
        <Card sx={{ p: 4, textAlign: 'center', borderRadius: 2 }}>
          <SchoolIcon sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
          <Typography color="text.secondary">No courses found. Create your first course to get started.</Typography>
        </Card>
      )}
      {!loading && courses.length > 0 && (
        <TableContainer component={Paper} elevation={0} sx={{ borderRadius: 2, border: '1px solid', borderColor: 'divider', overflow: 'hidden' }}>
          <Table size="medium">
            <TableHead>
              <TableRow sx={{ bgcolor: alpha('#0f766e', 0.08) }}>
                <TableCell sx={{ fontWeight: 700 }}>Course</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Category</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Content</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Enrollments</TableCell>
                <TableCell sx={{ fontWeight: 700 }}>Assessment</TableCell>
                <TableCell align="right" sx={{ fontWeight: 700 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {courses.map((c) => (
                <TableRow key={c.id} hover sx={{ '&:nth-of-type(even)': { bgcolor: 'action.hover' } }}>
                  <TableCell>
                    <Typography variant="body1" fontWeight={500}>{c.title}</Typography>
                    {c.description && <Typography variant="caption" color="text.secondary" display="block" noWrap sx={{ maxWidth: 240 }}>{c.description}</Typography>}
                  </TableCell>
                  <TableCell>{c.category || '—'}</TableCell>
                  <TableCell><Chip size="small" label={c.status} color={statusColor(c.status)} /></TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <MenuBookIcon fontSize="small" color="action" />
                      <span>{c.chapter_count ?? 0}</span>
                      <span style={{ color: '#94a3b8' }}>/</span>
                      <VideoLibraryIcon fontSize="small" color="action" />
                      <span>{c.video_count ?? 0}</span>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Button size="small" variant="outlined" startIcon={<GroupIcon />} onClick={() => openEnrollmentsModal(c)} sx={{ textTransform: 'none', fontWeight: 600 }}>
                      View enrollments ({c.enrollment_count ?? 0})
                    </Button>
                  </TableCell>
                  <TableCell>
                    {c.has_assessment ? <Chip size="small" icon={<QuizIcon sx={{ fontSize: 14 }} />} label={`${c.questions_count ?? 0} questions`} variant="outlined" /> : <Typography variant="caption" color="text.secondary">No</Typography>}
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Chapters & Videos"><IconButton size="small" onClick={() => openContentModal(c.id)}><MenuBookIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Assessment questions"><IconButton size="small" onClick={() => openQuestionsModal(c.id)}><QuizIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="View enrollments"><IconButton size="small" onClick={() => openEnrollmentsModal(c)}><GroupIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Edit"><IconButton size="small" onClick={() => openEdit(c)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                    <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => handleDeleteCourse(c.id)}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Enrollments modal - clearly visible list of who's enrolled */}
      <Dialog open={enrollmentsModalOpen} onClose={() => setEnrollmentsModalOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 40px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{ borderBottom: '1px solid', borderColor: 'divider', py: 2, bgcolor: alpha('#0f766e', 0.06) }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
            <GroupIcon sx={{ color: '#0f766e', fontSize: 28 }} />
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Enrollments</Typography>
              {enrollmentsCourse && (
                <Typography variant="body2" color="text.secondary">Course: {enrollmentsCourse.title}</Typography>
              )}
            </Box>
            {enrollmentsCourse && !enrollmentsLoading && (
              <Chip label={`${enrollments.length} enrolled`} size="medium" sx={{ ml: 'auto', fontWeight: 600 }} color="primary" variant="outlined" />
            )}
          </Box>
        </DialogTitle>
        <DialogContent sx={{ pt: 3, minHeight: 280 }}>
          {enrollmentsLoading && <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>}
          {!enrollmentsLoading && enrollments.length === 0 && (
            <Box sx={{ textAlign: 'center', py: 6 }}>
              <GroupIcon sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
              <Typography color="text.secondary" variant="body1">No one has enrolled in this course yet.</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>Enrollments will appear here when candidates enroll from the app.</Typography>
            </Box>
          )}
          {!enrollmentsLoading && enrollments.length > 0 && (
            <TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
              <Table size="medium">
                <TableHead>
                  <TableRow sx={{ bgcolor: alpha('#0f766e', 0.08) }}>
                    <TableCell sx={{ fontWeight: 700 }}>Candidate</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Contact</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Progress</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Score</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>Completed</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {enrollments.map((e) => (
                    <TableRow key={e.id} hover>
                      <TableCell><Typography fontWeight={500}>{e.candidate?.name || '—'}</Typography></TableCell>
                      <TableCell>{e.candidate?.email || e.candidate?.mobile || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={e.status} color={e.status === 'completed' ? 'success' : e.status === 'in_progress' ? 'primary' : 'default'} />
                      </TableCell>
                      <TableCell>{e.progress_percentage ?? 0}%</TableCell>
                      <TableCell>{e.score != null ? `${e.score}%` : '—'}</TableCell>
                      <TableCell>{e.completed_at ? new Date(e.completed_at).toLocaleDateString() : '—'}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button variant="contained" onClick={() => setEnrollmentsModalOpen(false)} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Create/Edit course dialog */}
      <Dialog open={createOpen} onClose={() => !submitting && setCreateOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>{editCourse ? 'Edit Course' : 'Create Course'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Title" value={form.title} onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))} margin="dense" />
          <TextField fullWidth multiline label="Description" value={form.description} onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))} margin="dense" rows={2} />
          <FormControl fullWidth margin="dense">
            <InputLabel>Content type</InputLabel>
            <Select value={form.content_type} label="Content type" onChange={(e) => setForm((f) => ({ ...f, content_type: e.target.value }))}>
              <MenuItem value="text">Text</MenuItem>
              <MenuItem value="video">Video</MenuItem>
              <MenuItem value="ppt">PPT</MenuItem>
              <MenuItem value="pdf">PDF</MenuItem>
            </Select>
          </FormControl>
          {form.content_type === 'video' && (
            <TextField fullWidth label="Video URL (YouTube or link)" value={form.video_url} onChange={(e) => setForm((f) => ({ ...f, video_url: e.target.value }))} margin="dense" />
          )}
          <TextField fullWidth type="number" label="Content duration (min)" value={form.content_duration} onChange={(e) => setForm((f) => ({ ...f, content_duration: e.target.value }))} margin="dense" />
          <TextField fullWidth type="number" label="Duration (hours)" value={form.duration_hours} onChange={(e) => setForm((f) => ({ ...f, duration_hours: e.target.value }))} margin="dense" />
          <TextField fullWidth label="Category" value={form.category} onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))} margin="dense" />
          <FormControl fullWidth margin="dense">
            <InputLabel>Level</InputLabel>
            <Select value={form.level} label="Level" onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}>
              <MenuItem value="beginner">Beginner</MenuItem>
              <MenuItem value="intermediate">Intermediate</MenuItem>
              <MenuItem value="advanced">Advanced</MenuItem>
            </Select>
          </FormControl>
          <FormControl fullWidth margin="dense">
            <InputLabel>Status</InputLabel>
            <Select value={form.status} label="Status" onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            type="number"
            label="Passing score %"
            value={form.passing_score}
            onChange={(e) => setForm((f) => ({ ...f, passing_score: e.target.value }))}
            margin="dense"
            helperText={form.has_assessment
              ? 'Minimum score on the final assessment to issue a certificate'
              : 'Also used for per-video quizzes (if you add them on videos)'}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={form.has_assessment}
                onChange={(e) => setForm((f) => ({ ...f, has_assessment: e.target.checked }))}
              />
            }
            label="Has final assessment (certificate after passing exam)"
            sx={{ mt: 1, display: 'block' }}
          />
          <Collapse in={form.has_assessment}>
            <Box
              sx={{
                mt: 1.5,
                mb: 1,
                p: 2,
                borderRadius: 2,
                bgcolor: alpha('#0f766e', 0.08),
                border: '1px solid',
                borderColor: alpha('#0f766e', 0.25)
              }}
            >
              <Alert severity="info" sx={{ mb: 2, bgcolor: 'transparent' }} icon={<QuizIcon />}>
                After candidates finish <strong>all videos</strong>, they take a final multiple-choice exam.
                They need at least <strong>{form.passing_score || 70}%</strong> to get the certificate.
              </Alert>
              {editCourse ? (
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<QuizIcon />}
                  onClick={() => {
                    const courseId = editCourse.id;
                    setCreateOpen(false);
                    openQuestionsModal(courseId);
                  }}
                  sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}
                >
                  Manage assessment questions ({editCourse.questions_count ?? 0} added)
                </Button>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Click <strong>Save</strong> — you will be taken to add assessment questions next (add at least 4–5 questions).
                </Typography>
              )}
            </Box>
          </Collapse>
          {!form.has_assessment && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
              No final exam: candidates get a certificate when all videos are complete. Use the Quiz icon on each video for optional per-video quizzes.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)} disabled={submitting}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveCourse} disabled={submitting}>{submitting ? 'Saving...' : 'Save'}</Button>
        </DialogActions>
      </Dialog>

      {/* Chapters & Videos dialog */}
      <Dialog open={contentModalOpen} onClose={() => !savingContent && setContentModalOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2, boxShadow: '0 8px 32px rgba(0,0,0,0.12)' } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700, color: '#0f766e' }}>
          <MenuBookIcon /> Chapters &amp; Videos
        </DialogTitle>
        <DialogContent sx={{ pt: 1 }}>
          {chaptersLoading && <CircularProgress size={28} sx={{ display: 'block', my: 2 }} />}
          <Box sx={{ mb: 3, p: 2, borderRadius: 2, bgcolor: alpha('#0f766e', 0.06), display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center' }}>
            <TextField size="small" label="New chapter title" value={newChapterTitle} onChange={(e) => setNewChapterTitle(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && addChapter()} sx={{ flex: 1, minWidth: 220 }} />
            <Button variant="contained" startIcon={<AddIcon />} onClick={addChapter} disabled={savingContent || !newChapterTitle.trim()} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>Add Chapter</Button>
          </Box>
          {chapters.map((ch) => (
            <Card key={ch.id} sx={{ p: 2.5, mb: 2, borderRadius: 2, border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 8px rgba(0,0,0,0.06)' }}>
              <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#0f766e' }}>
                <MenuBookIcon fontSize="small" /> {ch.title}
              </Typography>
              <Box sx={{ pl: 1 }}>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>Add video (name + Bunny Embed / Direct URL or YouTube link)</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, mb: 2 }}>
                  <TextField size="small" label="Video name" placeholder="e.g. Introduction to Safety" value={newVideoTitle[ch.id] || ''} onChange={(e) => setNewVideoTitle((prev) => ({ ...prev, [ch.id]: e.target.value }))} fullWidth />
                  <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', flexWrap: 'wrap' }}>
                    <TextField size="small" label="Bunny Embed or YouTube URL" placeholder="https://player.mediadelivery.net/embed/..." value={newVideoUrl[ch.id] || ''} onChange={(e) => setNewVideoUrl((prev) => ({ ...prev, [ch.id]: e.target.value }))} sx={{ flex: 1, minWidth: 260 }} />
                    <Button size="small" variant="contained" startIcon={<VideoLibraryIcon />} onClick={() => addVideoToChapter(ch.id)} disabled={savingContent || !(newVideoUrl[ch.id] || '').trim()} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>Add Video</Button>
                  </Box>
                </Box>
                {(ch.videos || []).length > 0 && (
                  <Box sx={{ mt: 2 }}>
                    <Typography variant="caption" fontWeight={600} color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>Videos in this chapter</Typography>
                    {(ch.videos || []).map((v) => (
                      <Box key={v.id} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, borderBottom: '1px solid', borderColor: 'divider', flexWrap: 'wrap' }}>
                        <VideoLibraryIcon fontSize="small" color="action" />
                        <Typography variant="body2" sx={{ flex: 1, minWidth: 120 }}>{v.title}</Typography>
                        {v.video_id && <Chip label="YouTube" size="small" sx={{ fontSize: 10 }} />}
                        {v.video_url && !v.video_id && (
                          <Chip
                            label={/\.m3u8|playlist/i.test(v.video_url || '') ? 'HLS' : 'Bunny'}
                            size="small"
                            color="primary"
                            sx={{ fontSize: 10 }}
                          />
                        )}
                        <Tooltip title="Video quiz questions">
                          <Button size="small" variant="outlined" startIcon={<QuizIcon />} onClick={() => openVideoQuizModal(v)} sx={{ textTransform: 'none' }}>
                            Quiz
                          </Button>
                        </Tooltip>
                      </Box>
                    ))}
                  </Box>
                )}
              </Box>
            </Card>
          ))}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider' }}>
          <Button onClick={() => setContentModalOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Per-video quiz questions */}
      <Dialog open={videoQuizModalOpen} onClose={() => !submitting && setVideoQuizModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle sx={{ fontWeight: 700 }}>
          Video quiz — {videoQuizVideo?.title || ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            Candidates must pass this quiz (course passing score) before marking the video complete. Optional &quot;Show at second&quot; for timed checkpoints in the app.
          </Typography>
          {videoQuizLoading && <CircularProgress size={24} sx={{ my: 2 }} />}
          {!videoQuizLoading && videoQuizQuestions.map((q) => (
            <Card key={q.id} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Typography variant="body2" fontWeight={600}>{q.question_text}</Typography>
              {q.time_in_video_seconds != null && (
                <Typography variant="caption" color="text.secondary">Checkpoint at {q.time_in_video_seconds}s</Typography>
              )}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">{(q.options || []).length} options</Typography>
                <IconButton size="small" color="error" onClick={() => deleteVideoQuestion(q.id)}><DeleteIcon /></IconButton>
              </Box>
            </Card>
          ))}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Add question</Typography>
          <TextField fullWidth multiline label="Question text" value={newVideoQuestionText} onChange={(e) => setNewVideoQuestionText(e.target.value)} margin="dense" />
          <TextField fullWidth type="number" label="Show at second (optional)" value={newVideoQuestionTime} onChange={(e) => setNewVideoQuestionTime(e.target.value)} margin="dense" helperText="Leave empty to show quiz after video" />
          {(newVideoQuestionOptions || []).map((o, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <TextField size="small" label={`Option ${i + 1}`} value={o.text} onChange={(e) => setNewVideoQuestionOptions((prev) => prev.map((opt, j) => (j === i ? { ...opt, text: e.target.value } : opt)))} sx={{ flex: 1 }} />
              <FormControlLabel control={<Checkbox checked={o.is_correct} onChange={(e) => setNewVideoQuestionOptions((prev) => prev.map((opt, j) => (j === i ? { ...opt, is_correct: e.target.checked } : { ...opt, is_correct: false })))} />} label="Correct" />
            </Box>
          ))}
          <Button size="small" onClick={() => setNewVideoQuestionOptions((prev) => [...prev, { text: '', is_correct: false }])} sx={{ mt: 1 }}>Add option</Button>
          <Box sx={{ mt: 2 }}>
            <Button variant="contained" onClick={addVideoQuestion} disabled={submitting || !newVideoQuestionText.trim()} sx={{ bgcolor: '#0f766e', '&:hover': { bgcolor: '#0d9488' } }}>
              Add question
            </Button>
          </Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setVideoQuizModalOpen(false)}>Close</Button></DialogActions>
      </Dialog>

      {/* Questions dialog */}
      <Dialog open={questionsModalOpen} onClose={() => !submitting && setQuestionsModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogTitle>Manage Assessment Questions</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" gutterBottom>Add at least 4–5 questions. Candidates must pass to receive a certificate.</Typography>
          {questionsLoading && <CircularProgress size={24} />}
          {!questionsLoading && questions.map((q) => (
            <Card key={q.id} sx={{ p: 2, mb: 2, borderRadius: 2 }}>
              <Typography variant="body2">{q.question_text}</Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 1 }}>
                <Typography variant="caption" color="text.secondary">{(q.options || []).length} options</Typography>
                <IconButton size="small" color="error" onClick={() => deleteQuestion(q.id)}><DeleteIcon /></IconButton>
              </Box>
            </Card>
          ))}
          <Typography variant="subtitle2" sx={{ mt: 2 }}>Add question</Typography>
          <TextField fullWidth multiline label="Question text" value={newQuestionText} onChange={(e) => setNewQuestionText(e.target.value)} margin="dense" />
          {(newQuestionOptions || []).map((o, i) => (
            <Box key={i} sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 1 }}>
              <TextField size="small" label={`Option ${i + 1}`} value={o.option_text} onChange={(e) => setNewQuestionOptions((prev) => prev.map((opt, j) => (j === i ? { ...opt, option_text: e.target.value } : opt)))} sx={{ flex: 1 }} />
              <FormControlLabel control={<Checkbox checked={o.is_correct} onChange={(e) => setNewQuestionOptions((prev) => prev.map((opt, j) => (j === i ? { ...opt, is_correct: e.target.checked } : opt)))} />} label="Correct" />
            </Box>
          ))}
          <Button size="small" onClick={() => setNewQuestionOptions((prev) => [...prev, { option_text: '', is_correct: false }])} sx={{ mt: 1 }}>Add option</Button>
          <Box sx={{ mt: 2 }}><Button variant="contained" onClick={addQuestion} disabled={submitting || !newQuestionText.trim()}>Add Question</Button></Box>
        </DialogContent>
        <DialogActions><Button onClick={() => setQuestionsModalOpen(false)}>Close</Button></DialogActions>
      </Dialog>
    </Box>
  );
}

export default AdminCourseManagement;
